import intersection from 'lodash/intersection';
import keyBy from 'lodash/keyBy';
import times from 'lodash/times';

import {
  CreateRegistrationInput,
  CreateUpgradeInput,
  GetCheckoutSummaryQuery as GetCheckoutSummary,
  GetEventQuery as GetEvent,
  ParticipantAttribute,
  ProductType,
  SavePurchaseInput,
} from '__generated__/graphql';

import { Charity } from '../../common/Fundraising/CharityPicker';
import { NestedTouchState } from '../../common/useTouchState';
import { sortedParticipantAttributes } from '../../common/helpers';

export type Event = GetEvent['event'];
export type TicketCategory = Event['ticket_categories'][0];
export type Ticket = TicketCategory['tickets_for_sale'][0];
export type TimeSlot = Ticket['upcoming_time_slots'][0];
export type Product = Event['products_for_sale'][0];
export type ProductVariant = Product['active_product_variants'][0];
export type Sellable = Ticket | Product;
export type Promotion = Ticket['promotions_for_sale'][0];

export type ParticipantField = Event['enabled_participant_fields'][0];
export type ParticipantFieldChoice = ParticipantField['enabled_choices'][0];

export type Purchase = SavePurchaseInput;

export type CouponCode = GetCheckoutSummary['checkoutSummary']['coupon_code'];

export enum CheckoutStep {
  Tickets = 'tickets',
  Personalisation = 'personalisation',
  Extras = 'extras',
  Team = 'team',
  Fundraising = 'fundraising',
  Details = 'details',
  Payment = 'payment',
}

export const SuccessStep = 'thank-you';

export interface CheckoutParams {
  eventId?: string;
  step?: CheckoutStep | typeof SuccessStep;
}

export interface Session {
  activeRegistration: number;
  invoiceDetails: boolean;
  agreedToTerms: boolean;
  donate: { [productId: string]: boolean; };
  touched: NestedTouchState;
  charities: { [charityId: string]: Charity; };
  /** Should only be present if it was passed via the URL. */
  preselectedCharityId: string | null;
  /** Should only be present if it was passed via the URL. */
  invitationCode: string | null;
  /** Should only be present if it was passed via the URL. */
  coupon: string | null;
  /** Should only be present if it was passed via the URL. */
  ticketCategoryIds: string[];
  queueToken: string | null;
  hideTimers: boolean;
}

export interface Queue {
  id: string;
  starting_at?: string | null;
  token_url: string;
}

export function getKeyedSellables<T extends Sellable>(sellables: T[]) {
  const result: { [promotionId: string]: T } = {};

  sellables.forEach((sellable) => {
    sellable.promotions_for_sale.forEach((promotion) => {
      result[promotion.id] = sellable;
    });
  });

  return result;
}

export function getKeyedTimeSlots(tickets: Ticket[]) {
  const result: { [timeSlotId: string]: TimeSlot } = {};

  tickets.forEach((ticket) => {
    ticket.upcoming_time_slots.forEach((timeSlot) => {
      result[timeSlot.id] = timeSlot;
    });
  });

  return result;
}

export function getKeyedProductVariants(products: Product[]) {
  const result: { [productVariantId: string]: ProductVariant } = {};

  products.forEach((product) => {
    product.active_product_variants.forEach((productVariant) => {
      result[productVariant.id] = productVariant;
    });
  });

  return result;
}

export const getTicketsForSale = (event: Event) => event.ticket_categories.reduce(
  (tickets, ticketCategory) => [...tickets, ...ticketCategory.tickets_for_sale],
  [] as Ticket[],
);

const filterProductPromotionsForSale = (product: Product, selectedPromotionIds: string[]) => ({
  ...product,
  promotions_for_sale: product.promotions_for_sale.filter((promotionForSale) => (
    promotionForSale.required_promotion_ids.length === 0
      || intersection(selectedPromotionIds, promotionForSale.required_promotion_ids).length > 0
  )),
});

export const getProductsForTicketPromotion = (promotion: { id: string; }, products: Product[]) => (
  products.map((product) => filterProductPromotionsForSale(product, [promotion.id]))
    .filter((product) => !product.stand_alone && product.promotions_for_sale.length > 0)
);

/**
 * Returns the list of non ticket fee products that are available with the given ticket promotion.
 */
export const getVisibleProductsForTicketPromotion = (promotion: { id: string; }, products: Product[]) => (
  getVisibleProducts(getProductsForTicketPromotion(promotion, products))
);

/**
 * Returns the list of non ticket fee products.
 */
export const getVisibleProducts = (products: Product[]) => (
  products.filter((product) => product.type === ProductType.item || product.type === ProductType.donation)
);

/**
 * Returns the list of stand-alone products.
 */
export const getStandAloneProducts = (
  selectedTicketPromotions: { id: string }[],
  selectedProducts: { id: string }[],
  products: Product[],
) => {
  const selectedTicketPromotionIds = selectedTicketPromotions.map(({ id }) => id);
  const selectedProductIds = selectedProducts.map(({ id }) => id);

  return products.map((product) => filterProductPromotionsForSale(product, selectedTicketPromotionIds))
    .filter((product) => {
      const requiredProductIds = product.products.map((product) => product.id);

      return (
        product.stand_alone
        && product.promotions_for_sale.length > 0
        && (requiredProductIds.length === 0 || intersection(requiredProductIds, selectedProductIds).length > 0)
      );
    });
};

/**
 * Creates upgrades for ticket fee products, and required products without choices.
 */
export const getAdditionalProducts = (
  ticketPromotion: Promotion,
  products: Product[],
  forBusinessTicket: boolean = false,
) => {
  const applicableProducts = getProductsForTicketPromotion(ticketPromotion, products);

  return applicableProducts.reduce((upgrades, product) => {
    if (
      ( // Ticket fees are not applied to free tickets
        product.type === ProductType.fee && ticketPromotion.amount > 0
      ) || (
        // Upgrades are applied if they are required and there is no choice to be made
        product.type === ProductType.item
          && product.min_per_ticket === product.max_per_ticket
          && product.promotions_for_sale.length === 1
          // For business tickets, variants are chosen later
          && (forBusinessTicket ? true : product.active_product_variants.length <= 1)
      )
    ) {
      return [
        ...upgrades,
        ...times(product.min_per_ticket, () => ({
          product: { id: product.id },
          product_variant: (
            product.active_product_variants.length > 0
              && (product.type === ProductType.fee || !forBusinessTicket)
          ) ? { id: product.active_product_variants[0].id } : null,
          purchase: { promotion: { id: product.promotions_for_sale[0].id } },
          fields: [],
        })),
      ] as CreateUpgradeInput[];
    }

    return upgrades;
  }, [] as CreateUpgradeInput[]);
};

interface TicketWithTeamFlags {
  allow_individuals: boolean;
  allow_create_team: boolean;
  allow_join_team: boolean;
}

export const getTeamFlags = (tickets: TicketWithTeamFlags[]) => {
  let allowIndividuals = true;
  let allowCreateTeam = false;
  let allowJoinTeam = false;
  let disallowJoinTeam = false;

  tickets.forEach((ticket) => {
    if (!ticket.allow_individuals) {
      allowIndividuals = false;
    }

    if (ticket.allow_create_team) {
      allowCreateTeam = true;

      if (!ticket.allow_join_team) {
        disallowJoinTeam = true;
      }
    }

    if (ticket.allow_join_team) {
      allowJoinTeam = true;
    }
  });

  allowJoinTeam = !disallowJoinTeam && allowJoinTeam;

  return { allowIndividuals, allowCreateTeam, allowJoinTeam };
};

/**
 * Calculates the set required participant attributes based on:
 * - Age restrictions on tickets
 * - Participant fields associated with the registrations
 * - Attributes that are enabled in the backend
 * - Attributes that are only enabled for certain tickets
 */
export const getRequiredParticipantAttributes = (
  registration: CreateRegistrationInput,
  event: Event,
) => {
  const ticket = getTicketsForSale(event).filter((ticket) => ticket.id === registration.ticket.id)[0];
  const hasTicketWithGenderRestriction = ticket?.restrictions.filter((restriction) => restriction.gender).length > 0;
  const hasTicketWithAgeRestriction = ticket?.restrictions.filter(
    (restriction) => restriction.min_age || restriction.max_age,
  ).length > 0;

  const keyedFields = keyBy(event.enabled_participant_fields, 'id');

  // Participant attributes that are required by participant fields of personal purchases.
  let requiredParticipantFieldAttributes: ParticipantAttribute[] = [];
  const fields: ParticipantField[] = [];

  registration.fields.forEach((fieldEntry) => {
    const field = keyedFields[fieldEntry.participant_field.id];

    if (field) {
      // Check if the field exists because they are loaded asynchronously
      fields.push(field);
    }
  });

  registration.upgrades.forEach((upgrade) => {
    upgrade.fields.forEach((fieldEntry) => {
      const field = keyedFields[fieldEntry.participant_field.id];

      if (field) {
        // Check if the field exists because they are loaded asynchronously
        fields.push(field);
      }
    });
  });

  fields.forEach((field) => {
    requiredParticipantFieldAttributes = [
      ...requiredParticipantFieldAttributes,
      ...field.required_participant_attributes,
    ];
  });

  // Take the attributes from the ticket, but only if activated on the event.
  const activatedParticipantAttributes = intersection(
    event.participant_attributes,
    ticket.participant_attributes,
  );

  return intersection(sortedParticipantAttributes, [
    ...activatedParticipantAttributes,
    ...requiredParticipantFieldAttributes,
    ...hasTicketWithAgeRestriction ? [ParticipantAttribute.date_of_birth] : [],
    ...hasTicketWithGenderRestriction ? [ParticipantAttribute.gender] : [],
  ]);
};

/**
 * Various ports of PHP code that validates TicketRestrictions.
 * The code tries to be close to the PHP original.
 */
export type TicketRestriction = Ticket['restrictions'][0];

// Port of DateHelper::minDateOfBirth()
const minDateOfBirth = (reference: Date, maxAge: number): Date => {
  const date = new Date(reference);
  date.setFullYear(date.getFullYear() - (maxAge + 1));
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

// Port of DateHelper::maxDateOfBirth()
const maxDateOfBirth = (reference: Date, minAge: number): Date => {
  const date = new Date(reference);
  date.setFullYear(date.getFullYear() - minAge);
  date.setHours(0, 0, 0, 0);
  return date;
};

// Port of TicketRestriction::meetsGender()
const meetsGender = (restriction: TicketRestriction, gender: string | null): boolean => (
  !restriction.gender || restriction.gender === gender
);

// Port of TicketRestriction::meetsMinimumAge()
export const meetsMinimumAge = (restriction: TicketRestriction, reference: Date, dateOfBirth: Date | null): boolean => {
  if (!restriction.min_age) {
    return true;
  }
  return dateOfBirth !== null && dateOfBirth <= maxDateOfBirth(reference, restriction.min_age);
};

// Port of TicketRestriction::meetsMaximumAge()
export const meetsMaximumAge = (restriction: TicketRestriction, reference: Date, dateOfBirth: Date | null): boolean => {
  if (!restriction.max_age) {
    return true;
  }
  return dateOfBirth !== null && dateOfBirth >= minDateOfBirth(reference, restriction.max_age);
};

// Port of Ticket::meetsTicketRestrictions()
// with inlined implementation of TicketRestriction::allows()
export const meetsTicketRestrictions = (
  restrictions: TicketRestriction[],
  reference: Date,
  gender: string | null,
  dateOfBirth: Date | null,
): boolean => {
  if (restrictions.length === 0) {
    return true;
  }
  return restrictions.some((restriction) => (
    meetsGender(restriction, gender)
    && meetsMinimumAge(restriction, reference, dateOfBirth)
    && meetsMaximumAge(restriction, reference, dateOfBirth)
  ));
};

// Port of Ticket::getAgeRestrictionsForGender()
export const getAgeRestrictionsForGender = (
  restrictions: TicketRestriction[],
  gender: string | null,
): TicketRestriction[] => (
  restrictions.filter((restriction) => (
    (!restriction.gender || restriction.gender === gender)
    && (restriction.min_age || restriction.max_age)
  ))
);

// Port of Ticket::hasGenderRestrictions()
export const hasGenderRestrictions = (restrictions: TicketRestriction[]): boolean => (
  restrictions.some((restriction) => !!restriction.gender)
);

export default undefined;
